import React, { useState } from "react";
import { IShow } from "@eagerdog/interfaces";
import moment from "moment-timezone";

import Button from "../Button/Button";
import Checkbox from "../Checkbox/Checkbox";

import "./CatalogShowSelectModal.scss";

interface IProps {
  shows: IShow[];
  /** Event timezone, used to group shows by calendar day. */
  timezone?: string;
  onDownload: (showIds: string[]) => void;
  onClose: () => void;
}

// "Trial 2" -> 2, "Show 1" -> 1; names without a number sort after numbered ones.
const showNumber = (show: IShow): number => {
  const m = (show.show_name || "").match(/(\d+)/);
  return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
};

// Order: show type, calendar day, trial/show number, then start time. Secretaries
// often set Trial 2 to start before Trial 1 on the same day (or leave times as
// placeholders), so sorting purely by show_date lists trials out of order.
const sortShows = (shows: IShow[], timezone?: string): IShow[] => {
  const day = (show: IShow) =>
    show.show_date
      ? (timezone ? moment(show.show_date).tz(timezone) : moment(show.show_date)).format("YYYY-MM-DD")
      : "";
  return [...shows].sort((a, b) => {
    if ((a.show_type || "") !== (b.show_type || "")) return (a.show_type || "").localeCompare(b.show_type || "");
    const dayA = day(a), dayB = day(b);
    if (dayA !== dayB) return dayA.localeCompare(dayB);
    const numA = showNumber(a), numB = showNumber(b);
    if (numA !== numB) return numA - numB;
    return moment(a.show_date || 0).valueOf() - moment(b.show_date || 0).valueOf();
  });
};

const CatalogShowSelectModal: React.FC<IProps> = (props) => {
  const shows = sortShows(props.shows, props.timezone);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(shows.map((s) => s._id))
  );

  const allSelected = selectedIds.size === shows.length;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(shows.map((s) => s._id)));
    }
  };

  const toggleShow = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  const getShowLabel = (show: IShow): string => {
    const date = show.show_date
      ? moment(show.show_date).format("M/D/YYYY")
      : "";
    const name = show.custom_show_name || show.show_name || "";
    return [show.show_type, date, name].filter(Boolean).join(" – ");
  };

  return (
    <div className="catalog-show-select-modal">
      <h2>Download Event Catalog</h2>
      <p>Select the shows/trials to include:</p>

      <div className="select-all-row">
        <Checkbox
          id="catalog-select-all"
          label={allSelected ? "Deselect All" : "Select All"}
          value={allSelected}
          onChange={toggleAll}
        />
      </div>

      <div className="show-list">
        {shows.map((show) => (
          <Checkbox
            key={show._id}
            id={`catalog-show-${show._id}`}
            label={getShowLabel(show)}
            value={selectedIds.has(show._id)}
            onChange={() => toggleShow(show._id)}
          />
        ))}
      </div>

      <div className="modal-actions">
        <Button className="secondary" onClick={props.onClose}>
          Cancel
        </Button>
        <Button
          disabled={selectedIds.size === 0}
          onClick={() =>
            props.onDownload(shows.filter((s) => selectedIds.has(s._id)).map((s) => s._id))
          }
        >
          Download
        </Button>
      </div>
    </div>
  );
};

export default CatalogShowSelectModal;
